import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryPageHeader from "@/components/dictionaries/DictionaryPageHeader";
import { useToast } from "@/components/ui/use-toast";
import { Module } from "@/constants";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import AgreementTypeForm from "@/modules/agreement-types/components/AgreementTypeForm";
import { useAgreementTypesDictionary } from "@/modules/agreement-types/hooks/useAgreementTypesDictionary";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { ApiError } from "@/services/api";
import { AgreementType, AgreementTypeCreate } from "@/types/agreementType";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutationFunction, useMutation } from "@tanstack/react-query";
import { exists } from "i18next";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export default function AgreementTypeCreator() {
  const id = "new";
  const { t } = useTranslation();
  const config = useAgreementTypesDictionary();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { invalidateListQuery } = useQueryCacheUpdater(Module.AgreementTypes);
  const labels = config.getLabels(t);
  const { can_create } = useModulePermissions();

  const mutationFn: MutationFunction<
    AgreementType,
    AgreementTypeCreate
  > = async (values) => {
    const response = await config.service.post(values);
    return response;
  };

  const mutation = useMutation<AgreementType, ApiError, AgreementTypeCreate>({
    mutationFn,
    onSuccess: (response) => {
      const id = response.id;
      navigate(`${config.path}/${id}`);
      toast({
        title: labels.success.create,
      });
      invalidateListQuery();
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: labels.errors.create,
        description,
        variant: "destructive",
      });
    },
  });

  const form = useForm<AgreementTypeCreate>({
    resolver: zodResolver(config.schema),
    defaultValues: (config.defaultValues ||
      {}) as DefaultValues<AgreementTypeCreate>,
    disabled: mutation.isPending || !can_create,
  });

  useFormUnsavedChangesWarning(form);

  return (
    <DictionaryRecordContextProvider
      value={{
        config,
        mutation,
        form,
        id,
      }}
    >
      <DictionaryPageHeader />
      <AgreementTypeForm />
    </DictionaryRecordContextProvider>
  );
}
